import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from "components/ui";
import { Field } from "components/ui/field";
import { Select } from "components/ui/select";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import {
  ApiDocType,
  serverService,
  ValidateSpecResponse,
} from "services/serverService";
import { getThemeColor } from "theme/colors";

interface Step1ApiDocumentationProps {
  onNext: (data: {
    docUrl: string;
    docType: ApiDocType;
    serverName: string;
    serverDescription: string;
    companyWebsite: string;
  }) => void;
  onBack: () => void;
  initialData?: {
    docUrl?: string;
    docType?: ApiDocType;
    serverName?: string;
    serverDescription?: string;
    companyWebsite?: string;
  };
}

// Derive a likely company website from the swagger/openapi URL.
// e.g. "https://api.billbee.io/Swagger/index.html" → "https://billbee.io"
const deriveCompanyWebsiteFromSpecUrl = (specUrl: string): string => {
  try {
    const url = new URL(specUrl);
    let host = url.hostname.replace(/^www\./i, "").replace(/^api\./i, "");
    return `${url.protocol}//${host}`;
  } catch {
    return "";
  }
};

export const Step1ApiDocumentation = ({
  onNext,
  onBack,
  initialData,
}: Step1ApiDocumentationProps) => {
  const { t } = useTranslation();
  const [docType, setDocType] = useState<ApiDocType>(
    initialData?.docType ?? ApiDocType.Swagger,
  );
  const [specUrl, setSpecUrl] = useState(initialData?.docUrl ?? "");
  const [companyWebsite, setCompanyWebsite] = useState(
    initialData?.companyWebsite ?? "",
  );
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidateSpecResponse | null>(null);
  const [hasChangedAfterValidation, setHasChangedAfterValidation] =
    useState(false);
  const [isProceeding, setIsProceeding] = useState(false);

  const handleValidate = async () => {
    if (!specUrl) {
      toaster.create({
        title: t("app.onboarding.step1.specRequired"),
        type: "error",
      });
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const result = await serverService.validateSpec({
        specUrl,
        apiDocType: docType,
      });
      setValidationResult(result);
      setHasChangedAfterValidation(false);

      if (result.isValid) {
        // The user may have pasted the documentation page rather than the spec itself. The API
        // resolves the real spec URL in that case — show it, so what we create matches what they see.
        const effectiveSpecUrl =
          result.specUrlWasResolved && result.resolvedSpecUrl
            ? result.resolvedSpecUrl
            : specUrl;

        if (effectiveSpecUrl !== specUrl) {
          setSpecUrl(effectiveSpecUrl);
        }

        // Smart pre-fill: derive a likely company website from the spec URL on first successful
        // validation. User can override; if they already entered one, we keep it.
        if (!companyWebsite) {
          const derived = deriveCompanyWebsiteFromSpecUrl(effectiveSpecUrl);
          if (derived) setCompanyWebsite(derived);
        }
        toaster.create({
          title: t("app.onboarding.step1.validationSuccess"),
          description: t("app.onboarding.step1.validationSuccessDesc", {
            count: result.estimatedToolCount,
          }),
          type: "success",
        });
      } else {
        toaster.create({
          title: t("app.onboarding.step1.validationFailed"),
          description: result.message,
          type: "error",
        });
      }
    } catch (error: any) {
      toaster.create({
        title: t("app.onboarding.step1.validationError"),
        description:
          error.message || t("app.onboarding.step1.validationErrorDesc"),
        type: "error",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleNext = () => {
    if (!validationResult?.isValid) {
      toaster.create({
        title: t("app.onboarding.step1.validateFirst"),
        type: "warning",
      });
      return;
    }

    if (!companyWebsite.trim()) {
      toaster.create({
        title: t("app.onboarding.step1.websiteRequired"),
        type: "warning",
      });
      return;
    }

    setIsProceeding(true);
    onNext({
      docUrl: specUrl,
      docType,
      serverName: validationResult.serverName || "API Server",
      serverDescription: validationResult.serverDescription || "API Server",
      companyWebsite: companyWebsite.trim(),
    });
  };

  const handleSpecUrlChange = (value: string) => {
    setSpecUrl(value);
    if (validationResult) {
      setHasChangedAfterValidation(true);
      setValidationResult(null);
    }
  };

  const handleDocTypeChange = (value: string[]) => {
    setDocType(parseInt(value[0]) as ApiDocType);
    if (validationResult) {
      setHasChangedAfterValidation(true);
      setValidationResult(null);
    }
  };

  const isValidated = validationResult?.isValid && !hasChangedAfterValidation;

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={4}>
        <VStack gap={4} align="stretch">
          {/* Documentation Type Selector */}
          <Select
            label={t("app.onboarding.step1.docType")}
            required
            items={[
              {
                label: t("app.onboarding.step1.swagger"),
                value: ApiDocType.Swagger.toString(),
              },
              {
                label: t("app.onboarding.step1.readme"),
                value: ApiDocType.Readme.toString(),
              },
            ]}
            value={[docType.toString()]}
            onValueChange={(e: { value: string[] }) =>
              handleDocTypeChange(e.value)
            }
            size="md"
          />

          {/* Spec URL Input */}
          <Field
            label={t("app.onboarding.step1.specUrl")}
            helperText={
              docType === ApiDocType.Swagger
                ? t("app.onboarding.step1.specUrlHelper")
                : t("app.onboarding.step1.specUrlHelperReadme")
            }
            required
          >
            <Input
              value={specUrl}
              onChange={(e) => handleSpecUrlChange(e.target.value)}
              placeholder={t("app.onboarding.step1.enterSpecUrl")}
              size="md"
            />
          </Field>

          {/* Shown when the user pasted a documentation page and we swapped in the real spec URL. */}
          {isValidated && validationResult?.specUrlWasResolved && (
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.onboarding.step1.specUrlResolved")}
            </Text>
          )}

          {/* Company website — required, used to generate domain-aware guardrails. */}
          <Field
            label={t("app.onboarding.step1.companyWebsite")}
            helperText={t("app.onboarding.step1.companyWebsiteHelper")}
            required
          >
            <Input
              type="url"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder={t("app.onboarding.step1.companyWebsitePlaceholder")}
              size="md"
            />
          </Field>

          {/* Validation Result */}
          {isValidating && (
            <Box textAlign="center" py={4}>
              <Spinner size="lg" color="blue.500" />
              <Text mt={2} color={getThemeColor("text.secondary")}>
                {t("app.onboarding.step1.validating")}
              </Text>
            </Box>
          )}

          {validationResult && validationResult.isValid && (
            <Box
              borderWidth="1px"
              borderColor={getThemeColor("alert.success.border")}
              bg={getThemeColor("alert.success.bg")}
              borderRadius="lg"
              p={4}
            >
              <HStack justify="space-between" mb={4} align="start">
                <Box>
                  <Heading
                    size="sm"
                    color={getThemeColor("alert.success.text")}
                    mb={1}
                  >
                    ✓ {t("app.onboarding.step1.specificationValid")}
                  </Heading>
                  <Text
                    fontSize="xs"
                    color={getThemeColor("alert.success.text")}
                  >
                    {t("app.onboarding.step1.validationSuccessMessage")}
                  </Text>
                </Box>
                <Badge colorPalette="green" size="lg" px={3} py={1}>
                  {t("app.onboarding.step1.endpointsCount", {
                    count: validationResult.estimatedToolCount,
                  })}
                </Badge>
              </HStack>

              <VStack gap={3} align="stretch">
                <Box
                  bg={getThemeColor("bg.secondary")}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={getThemeColor("border.default")}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={getThemeColor("text.secondary")}
                    mb={1}
                  >
                    {t("app.onboarding.step1.serverNameLabel")}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={getThemeColor("text.primary")}
                  >
                    {validationResult.serverName}
                  </Text>
                </Box>

                <Box
                  bg={getThemeColor("bg.secondary")}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={getThemeColor("border.default")}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={getThemeColor("text.secondary")}
                    mb={1}
                  >
                    {t("app.onboarding.step1.descriptionLabel")}
                  </Text>
                  <Text fontSize="sm" color={getThemeColor("text.primary")}>
                    {validationResult.serverDescription}
                  </Text>
                </Box>

                <Box
                  bg={getThemeColor("bg.secondary")}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={getThemeColor("border.default")}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={getThemeColor("text.secondary")}
                    mb={1}
                  >
                    {t("app.onboarding.step1.toolsCountLabel")}
                  </Text>
                  <HStack gap={2}>
                    <Text fontSize="lg" fontWeight="bold" color="teal.600">
                      {validationResult.estimatedToolCount}
                    </Text>
                    <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                      {t("app.onboarding.step1.apiEndpointsDetected")}
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </Box>
          )}

          {validationResult && !validationResult.isValid && (
            <Box
              borderWidth="1px"
              borderColor={getThemeColor("alert.error.border")}
              bg={getThemeColor("alert.error.bg")}
              borderRadius="lg"
              p={4}
            >
              <Heading
                size="sm"
                color={getThemeColor("alert.error.text")}
                mb={2}
              >
                ✗ {t("app.onboarding.step1.validationFailed")}
              </Heading>
              <Text color={getThemeColor("alert.error.text")} fontSize="sm">
                {validationResult.message}
              </Text>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Sticky Footer */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        bg={getThemeColor("modal.bg")}
        py={4}
        px={6}
        boxShadow="sm"
      >
        <HStack justify="space-between">
          <Button variant="ghost" onClick={onBack} size="md">
            {t("app.onboarding.step1.back")}
          </Button>
          {isValidated ? (
            <Button
              colorPalette="blue"
              onClick={handleNext}
              size="md"
              loading={isProceeding}
              disabled={isProceeding || !companyWebsite.trim()}
            >
              {t("app.onboarding.step1.next")}
            </Button>
          ) : (
            <Button
              colorPalette="blue"
              onClick={handleValidate}
              loading={isValidating}
              disabled={!specUrl || isValidating}
              size="md"
            >
              {t("app.onboarding.step1.validate")}
            </Button>
          )}
        </HStack>
      </Box>
    </Box>
  );
};
